import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import { useTinycmsSites } from '../cms/sites';
import { showInkToast } from '../cms/InkToast';
import { useChannelReconnect } from './ChannelPicker';
import { ChannelLogo } from './ChannelLogo';
import { PostPanel } from './PostPanel';
import { ApprovalChip } from '../settings/AgentControls';
import { APPROVALS_PATH, approvalsAt, channelAnchor } from '../settings/goesOut';
import { useFreshPost } from './FreshPost';
import { todayWindow, useReachDecisions } from '../cms/todayData';
import {
  connectionLapsed,
  missedGroupWords,
  reconnectWords,
  retryLabel,
  sharedCause,
  staleReason,
} from '../cms/missedPost';
import {
  approvalWords,
  Channel,
  channelKind,
  channelName,
  isPersonalProfile,
  DISCONNECT_SOCIAL_CHANNEL,
  channelStatCells,
  connectionExpired,
  expiredWords,
  lastPostProblem,
  postToFix,
  POSTABLE,
  postText,
  postTime,
  REACH_CHANNEL_ROWS,
  ReachPost,
  RETRY_SOCIAL_POST,
  SKIP_SOCIAL_POST,
  waitingForApproval,
  publishingOff,
  SET_CHANNEL_PUBLISHING,
} from './reach';
import { REACH_SITE_VISITS, ReachVisits } from './reachVisits';
import { askInk } from '../cms/InkConfirm';
import { ACCOUNT_VOICE, AccountVoice } from './voiceWords';
import { CopyEditChip } from '../settings/CopyEditChip';

/** Platforms where one sign-in is one account, so a connected one leaves the Add list. */
const ONE_PER_SIGN_IN = new Set(['x', 'twitter', 'threads', 'tiktok']);

/**
 * Reach → Channels ("tinycms UX optimization review v6", Prototype v6): the
 * connected social accounts, each with whether the agent asks first or posts on
 * its own, and the channels that can still be added.
 *
 * Connecting: the sign-in popup comes back with the channels it can post to
 * (a Facebook sign-in may hold several Pages and their Instagram accounts);
 * the person picks, and only then do rows exist (useChannelConnect).
 * Disconnecting removes the channel's stored credentials (nothing of our
 * access is left behind) and keeps the row (is_active = false) so its posts
 * stay attached; connecting the same page again brings that row back with a
 * fresh sign-in. Reconnect signs that same channel in again and replaces the
 * credentials, without removing the row first.
 *
 * Health (Prototype v16): "3 posts waiting for a decision · 29 older, not
 * posted" under its name — the same posts, in the same window, as Today — and
 * Review opens them under the row: why, each post (with what makes it old news
 * or that it links off the site), then Skip all and Post now, or Write a fresh
 * post first when they're old news.
 */
/** Whose name a personal profile posts under: "Hai Tran". */
const personName = (c: Channel) => (c.profileName || c.profileUsername || 'you').replace(/^@/, '');

/** A channel's Reconnect and Disconnect, out of the way until they're needed. */
function ChannelMenu({
  name,
  disabled,
  onReconnect,
  onDisconnect,
}: {
  name: string;
  disabled: boolean;
  onReconnect: () => void;
  onDisconnect: () => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const pick = (act: () => void) => () => {
    setOpen(false);
    act();
  };
  return (
    <div className="rch-menu" ref={box}>
      <button
        type="button"
        className="rch-menu-btn"
        aria-label={`More for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open ? (
        <div className="ink-menu rch-menu-list" role="menu">
          <button type="button" role="menuitem" onClick={pick(onReconnect)}>
            Reconnect
          </button>
          <button type="button" role="menuitem" onClick={pick(onDisconnect)}>
            Disconnect
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** "Disconnected Oct 12 · posts paused". */
const disconnectedWords = (at?: string | null) =>
  `Disconnected${at ? ` ${format(new Date(at), 'MMM d')}` : ''} · posts paused`;

export function ReachChannels() {
  const { accountId, selectedAccount } = useAccount();
  const { sites } = useTinycmsSites();
  const siteName =
    sites.find((s) => s.accountId === accountId)?.name || selectedAccount?.name || 'your site';
  // A personal profile set to post on its own asks first, in the row (Prototype v14).
  // Today's window, so "waiting for a decision" counts what Today lists.
  const since = todayWindow('week').from;
  const { data, refetch } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNEL_ROWS, {
    variables: { accountId, since },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Clicks to site, last 30 days per channel: visits from each channel's post
  // links, from the site's Google Analytics as last synced (reachVisits.ts).
  const { data: visitData } = useQuery<{ reachSiteVisits: ReachVisits }>(REACH_SITE_VISITS, {
    variables: { accountId, days: 30 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const visits30 = visitData?.reachSiteVisits;
  const clicksLive = Boolean(visits30?.connected && visits30.syncedAt);
  // Posts waiting for approval, as Today and the Calendar count them.
  const { waitingPosts } = useReachDecisions();
  const clicksBy = new Map((visits30?.channels || []).map((v) => [v.socialProfileId, v.visits]));
  const [setPublishing] = useMutation(SET_CHANNEL_PUBLISHING);
  const { data: voiceData } = useQuery<{ accountVoice: AccountVoice }>(ACCOUNT_VOICE, {
    variables: { accountId },
    skip: !accountId,
  });
  const voice = voiceData?.accountVoice || null;
  const [disconnectChannel] = useMutation(DISCONNECT_SOCIAL_CHANNEL);
  const [retry] = useMutation(RETRY_SOCIAL_POST);
  const [skip] = useMutation(SKIP_SOCIAL_POST);
  const [busy, setBusy] = useState<string | null>(null);
  const [fixing, setFixing] = useState<ReachPost | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);
  // ?review=<channel id> (the Calendar's "didn't go out" strip): that
  // channel's list opens, in view, once the rows are here.
  const [params, setParams] = useSearchParams();
  const reviewParam = params.get('review');
  useEffect(() => {
    if (!reviewParam || !data) return;
    setReviewing(reviewParam);
    const next = new URLSearchParams(params);
    next.delete('review');
    setParams(next, { replace: true });
    requestAnimationFrame(() =>
      document
        .getElementById(`rch-${reviewParam}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    );
  }, [reviewParam, data, params, setParams]);
  const fresh = useFreshPost(() => void refetch());

  /** Every post in the list at once: Skip all / Post now. */
  const decideAll = async (c: Channel, list: ReachPost[], kind: 'skip' | 'retry') => {
    setBusy(c.id);
    const words = missedGroupWords(
      channelName(c.platform),
      list.map(postTime).filter((t): t is Date => Boolean(t)),
      list[0]?.failure
    );
    try {
      const res = await Promise.allSettled(
        list.map((p) =>
          kind === 'retry' ? retry({ variables: { id: p.id } }) : skip({ variables: { id: p.id } })
        )
      );
      const bad = res.filter((r) => r.status === 'rejected').length;
      if (bad)
        showInkToast(`${list.length - bad} of ${list.length} went through. Try the rest again.`);
      else if (list.length === 1)
        showInkToast(
          kind === 'retry' ? 'Trying again. It goes out in a minute.' : 'Skipped. It won’t go out.'
        );
      else showInkToast(kind === 'retry' ? words.posted : words.skipped);
      setReviewing(null);
      await refetch();
    } finally {
      setBusy(null);
    }
  };
  const { start, reconnect, addBluesky, dialogs } = useChannelReconnect({
    accountId,
    onConnected: () => void refetch(),
  });

  const channels = (data?.socialProfiles || []).filter((c) => c.isActive);
  // Stay on the list with the way back, unless the same account is connected again.
  const disconnected = (data?.socialProfiles || []).filter(
    (c) =>
      !c.isActive && !channels.some((a) => a.platform === c.platform && a.profileId === c.profileId)
  );
  const connectedPlatforms = new Set(channels.map((c) => c.platform.toLowerCase()));

  const setChannelOn = async (c: Channel, on: boolean) => {
    setBusy(c.id);
    try {
      await setPublishing({ variables: { id: c.id, on } });
      await refetch();
      showInkToast(
        on
          ? `Publishing is on for ${channelName(c.platform)}.`
          : `Publishing is off for ${channelName(c.platform)}. Nothing will post to it.`,
        {
          label: 'Undo',
          onClick: () => void setChannelOn(c, !on),
          ms: 6000,
        }
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (c: Channel) => {
    if (
      !(await askInk({
        title: `Disconnect ${channelName(c.platform)}?`,
        body: 'Your sign-in is removed and nothing more is posted there until you connect it again.',
        confirmLabel: 'Disconnect',
        danger: true,
      }))
    )
      return;
    setBusy(c.id);
    try {
      await disconnectChannel({ variables: { id: c.id } });
      await refetch();
      showInkToast(`${channelName(c.platform)} disconnected. Your sign-in has been removed.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const fix = async (post: ReachPost, kind: 'approve' | 'skip' | 'retry') => {
    if (kind === 'approve') return;
    setBusy(post.id);
    try {
      if (kind === 'retry') {
        await retry({ variables: { id: post.id } });
        showInkToast('Trying again. It goes out in a minute.');
      } else {
        await skip({ variables: { id: post.id } });
        showInkToast('Skipped. It won’t go out.');
      }
      setFixing(null);
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const addable = POSTABLE.filter(
    (p) =>
      !ONE_PER_SIGN_IN.has(p) ||
      !(connectedPlatforms.has(p) || (p === 'x' && connectedPlatforms.has('twitter')))
  );

  return (
    <div className="rch">
      <section>
        <h2 className="rch-h">Your channels</h2>
        <p className="ink-muted rch-sub">
          Whether posts ask you first is set in <Link to={APPROVALS_PATH}>Approvals</Link>.
        </p>
        {channels.length || disconnected.length ? (
          <div className="rch-list">
            {channels.map((c) => {
              const problem = lastPostProblem(c);
              const expired = connectionExpired(c);
              const toApprove = waitingForApproval(c, data?.socialProfiles || [], waitingPosts);
              // An expired channel's numbers aren't current (Prototype round 20).
              const stat = channelStatCells(expired ? null : c.stats);
              return (
                <div className="rch-row" key={c.id} id={`rch-${c.id}`}>
                  <ChannelLogo platform={c.platform} className="rc-mark rch-logo" />
                  <div className="rch-name">
                    <div>{channelName(c.platform)}</div>
                    {/* "Account · @montecarlo", "Personal profile · Hai Tran" (Prototype v14). */}
                    <div className="ink-muted">
                      {[
                        channelKind(c.platform, c.profileId),
                        // A handle gets its @; older Facebook rows stored the Page's
                        // name here ("Space Interests"), which isn't one.
                        c.profileUsername
                          ? /\s/.test(c.profileUsername.trim())
                            ? c.profileUsername.trim()
                            : `@${c.profileUsername.replace(/^@/, '')}`
                          : c.profileName || '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {isPersonalProfile(c.platform, c.profileId) && c.approvalMode === 'auto' ? (
                      <div className="rch-personal">
                        Posts as {personName(c)}, not as {siteName} ·{' '}
                        <button type="button" onClick={() => void start(c.platform.toLowerCase())}>
                          Connect the company Page
                        </button>
                      </div>
                    ) : null}
                    {c.metricsNeedReconnect ? (
                      // An Instagram account connected through its Facebook Page before
                      // the sign-in asked for instagram_manage_insights: its posts' views
                      // come through once it signs in again.
                      <div className="rch-personal">
                        Reconnect to see views ·{' '}
                        <button type="button" onClick={() => void start('facebook')}>
                          Reconnect through Facebook
                        </button>
                      </div>
                    ) : null}
                    {problem && c.missedPosts?.posts?.length ? (
                      <div className="rch-problem">
                        {problem} ·{' '}
                        <button
                          type="button"
                          aria-expanded={reviewing === c.id}
                          onClick={() => setReviewing(reviewing === c.id ? null : c.id)}
                        >
                          {reviewing === c.id ? 'Hide' : 'Review'}
                        </button>
                      </div>
                    ) : problem && postToFix(c) ? (
                      // An older server without the list: the latest post, as before.
                      <div className="rch-problem">
                        {problem} ·{' '}
                        <button type="button" onClick={() => setFixing(postToFix(c))}>
                          Fix
                        </button>
                      </div>
                    ) : null}
                    {toApprove.length ? (
                      // Its own line, beside any that didn't go out (QA round 23):
                      // one opens its approval page; several, the Calendar's list.
                      <div className="rch-approval">
                        {approvalWords(toApprove.length)} ·{' '}
                        <Link
                          to={
                            toApprove.length === 1
                              ? `/reach/approve/${toApprove[0].id}`
                              : '/reach/calendar'
                          }
                        >
                          Review
                        </Link>
                      </div>
                    ) : null}
                    {reviewing === c.id && c.missedPosts?.posts?.length ? (
                      <MissedReview
                        channel={c}
                        posts={c.missedPosts.posts}
                        busy={busy === c.id}
                        onOpen={setFixing}
                        onAll={(kind) => void decideAll(c, c.missedPosts!.posts!, kind)}
                        onReconnect={() => reconnect(c)}
                        onFresh={(p) => void fresh.open(p)}
                      />
                    ) : null}
                  </div>
                  {/* Followers, 30 days and Clicks to site (Prototype v14). */}
                  <div
                    className="rch-stats"
                    title={c.stats ? `As of ${c.stats.asOf}` : 'Not collected for this channel yet'}
                  >
                    <span>Followers</span>
                    <span>30 days</span>
                    <span>Clicks to site</span>
                    <b>{stat.followers}</b>
                    <b data-tone={stat.growthTone}>{stat.growth}</b>
                    <b
                      data-tone={clicksLive ? undefined : 'none'}
                      title={
                        clicksLive
                          ? 'Visits to your site from this channel’s posts, last 30 days'
                          : 'Connect Google Analytics in Website → Analytics to count clicks'
                      }
                    >
                      {clicksLive && !expired ? (clicksBy.get(c.id) || 0).toLocaleString() : '—'}
                    </b>
                  </div>
                  <button
                    type="button"
                    className="rch-publishing"
                    role="switch"
                    aria-checked={!publishingOff(c)}
                    aria-label={`Publishing to ${channelName(c.platform)}`}
                    disabled={busy === c.id}
                    onClick={() => void setChannelOn(c, publishingOff(c))}
                  >
                    <span aria-hidden="true">Publishing</span>
                    <span
                      className="ink-profile-switch"
                      data-on={publishingOff(c) ? 'false' : 'true'}
                    />
                  </button>
                  <ApprovalChip
                    mode={c.approvalMode === 'auto' ? 'out' : 'ask'}
                    to={approvalsAt(channelAnchor(c.id))}
                  />
                  <ChannelMenu
                    name={channelName(c.platform)}
                    disabled={busy === c.id}
                    onReconnect={() => reconnect(c)}
                    onDisconnect={() => void disconnect(c)}
                  />
                  {publishingOff(c) ? (
                    <div className="rch-off" role="status">
                      Publishing is off. Nothing posts to this channel, and scheduled posts won’t go
                      out.
                    </div>
                  ) : null}
                  {expired ? (
                    // Its sign-in ran out: say so on the row Today sends people
                    // to, with the fix as the main action (UX review round 20).
                    <div className="rch-expired" role="status">
                      <span className="rch-expired-dot" aria-hidden="true" />
                      <span className="rch-expired-text">
                        {expiredWords(c.missedPosts?.count || 0)}
                      </span>
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        disabled={busy === c.id}
                        onClick={() => reconnect(c)}
                      >
                        Reconnect
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {disconnected.map((c) => (
              <div className="rch-row" key={c.id} id={`rch-${c.id}`} data-disconnected="true">
                <ChannelLogo platform={c.platform} className="rc-mark rch-logo" />
                <div className="rch-name">
                  <div>{channelName(c.platform)}</div>
                  <div className="ink-muted">
                    {[
                      channelKind(c.platform, c.profileId),
                      c.profileUsername || c.profileName || '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <div className="rch-gone" role="status">
                    {disconnectedWords(c.updatedAt)}
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-btn rch-gone-btn"
                  disabled={busy === c.id}
                  onClick={() => reconnect(c)}
                >
                  Reconnect
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="rch-none">No channels connected yet.</p>
        )}
        {voice?.socialChecks ? (
          <div className="rch-copy-edit">
            <CopyEditChip required={voice.socialCopyEdit} />
          </div>
        ) : null}
      </section>

      <section style={{ marginTop: 36 }}>
        <h2 className="rch-h">Add a channel</h2>
        <div className="rch-add">
          {addable.map((p) => (
            <button
              key={p}
              type="button"
              className="rch-add-btn"
              disabled={!accountId}
              onClick={() => (p === 'bluesky' ? addBluesky() : void start(p))}
            >
              <ChannelLogo platform={p} className="rc-mark rc-mark-sm" />
              {channelName(p)}
              {connectedPlatforms.has(p) ? <span className="ink-muted"> · add another</span> : null}
            </button>
          ))}
        </div>
        <p className="ink-muted rch-sub" style={{ marginTop: 10 }}>
          You’ll sign in to the channel in a new window. One Facebook sign-in covers your Pages and
          the Instagram accounts linked to them, and a LinkedIn sign-in covers your profile and the
          Pages you administer; you pick which to connect. Bluesky uses an app password instead.
        </p>
      </section>

      {dialogs}
      {fresh.node}
      {fixing ? (
        <PostPanel
          post={fixing}
          busy={busy === fixing.id}
          onClose={() => setFixing(null)}
          // Only a missed post is opened here; the draft actions never show.
          onAct={(kind) => {
            if (kind === 'approve' || kind === 'skip' || kind === 'retry') void fix(fixing, kind);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Channels → Review (Prototype v16): a channel's posts waiting for a decision,
 * the same list Today groups — why they didn't go out, each post with what
 * makes it old news or that it links off the site, then the decision for all
 * of them. Old news leads with Skip and offers a fresh post instead of Post now.
 */
function MissedReview({
  channel,
  posts,
  busy,
  onOpen,
  onAll,
  onReconnect,
  onFresh,
}: {
  channel: Channel;
  posts: ReachPost[];
  busy: boolean;
  onOpen: (p: ReachPost) => void;
  onAll: (kind: 'skip' | 'retry') => void;
  onReconnect: () => void;
  onFresh: (p: ReachPost) => void;
}) {
  const name = channelName(channel.platform);
  const times = posts.map(postTime).filter((t): t is Date => Boolean(t));
  const words = missedGroupWords(name, times, posts[0]?.failure);
  const one = posts.length === 1;
  const stale = posts.find((p) => staleReason(postText(p), postTime(p))) || null;
  const failure = posts[0]?.failure;
  const sameCause = failure && posts.every((p) => p.failure === failure);
  const fmt = (d: Date | null) =>
    d
      ? `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(
          [],
          {
            hour: 'numeric',
            minute: '2-digit',
          }
        )}`
      : '';
  return (
    <div className="rch-review">
      {sameCause ? (
        <p className="rch-review-cause">
          {connectionLapsed(failure)
            ? `Why: ${reconnectWords(name)}`
            : one
              ? `Why: ${failure}.`
              : `Why: ${sharedCause(failure!, name)}.`}
        </p>
      ) : null}
      <ul className="rch-review-list">
        {posts.map((p) => {
          const old = staleReason(postText(p), postTime(p));
          return (
            <li key={p.id}>
              <button type="button" className="rch-review-post" onClick={() => onOpen(p)}>
                <span className="rch-review-when">{fmt(postTime(p))}</span>
                <span className="rch-review-text">
                  {postText(p).split('\n')[0] || 'No caption'}
                </span>
                {old ? <span className="rch-review-warn">{old}</span> : null}
                {p.offSiteDomain ? (
                  <span className="rch-review-warn">Links to {p.offSiteDomain}, not your site</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="rch-review-actions">
        {stale ? (
          <>
            <button
              type="button"
              className="ink-btn"
              disabled={busy}
              onClick={() => onFresh(stale)}
            >
              Write a fresh post
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => onAll('skip')}
            >
              {one ? 'Skip' : words.skip}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ink-btn" disabled={busy} onClick={() => onAll('skip')}>
              {one ? 'Skip' : words.skip}
            </button>
            {connectionLapsed(failure) ? (
              // Backlog 8def3fa5: trying again fails until it's signed in again.
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={busy}
                onClick={onReconnect}
              >
                Reconnect {name}
              </button>
            ) : (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={busy}
                onClick={() => onAll('retry')}
              >
                {one ? retryLabel(postTime(posts[0])) : words.cta}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
